import { useEffect, useState } from "react";
import { STATUS_ANNOUNCE_DELAY_MS, STATUS_ROTATE_MS } from "./constants";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * What a visitor or client reads while an answer is prepared. Deliberately says nothing about HOW the answer is
 * found (no searching, documents, sources or knowledge): product decision. Staff-facing surfaces (playground,
 * workflow preview) keep the stage wording instead, through `audience="staff"`.
 */
export const NEUTRAL_STATUS_PHRASES = ["Thinking…", "Working on it…", "Putting that together…", "Almost there…"] as const;

/** Phrase for `tick`, wrapping around; "" for an empty list. */
export function statusPhraseAt(phrases: readonly string[], tick: number): string {
  if (phrases.length === 0) return "";
  return phrases[tick % phrases.length] ?? "";
}

export interface RotatingStatus {
  /** The phrase to show now. */
  phrase: string;
  /**
   * Text for the ONE polite live region: empty on the first render (a region that already holds text when it is
   * inserted is not announced by every screen reader), then the first phrase. It never follows the rotation, so
   * the visitor hears the status once, not every 2.5 s.
   */
  announcement: string;
}

/**
 * The phrase to show now: the list rotates every `intervalMs` while the component is mounted. Under
 * `prefers-reduced-motion: reduce` it never rotates (the first phrase stays), also when the preference changes live.
 */
export function useRotatingStatus(phrases: readonly string[], intervalMs: number = STATUS_ROTATE_MS): RotatingStatus {
  const [tick, setTick] = useState(0);
  const [announced, setAnnounced] = useState(false);
  const count = phrases.length;
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAnnounced(true);
    }, STATUS_ANNOUNCE_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, []);
  useEffect(() => {
    if (count < 2) return undefined;
    const query = typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION_QUERY) : null;
    let timer: number | undefined;
    const stop = () => {
      if (timer === undefined) return;
      window.clearInterval(timer);
      timer = undefined;
    };
    const sync = () => {
      stop();
      if (query?.matches) {
        setTick(0);
        return;
      }
      timer = window.setInterval(() => {
        setTick((n) => n + 1);
      }, intervalMs);
    };
    sync();
    query?.addEventListener("change", sync);
    return () => {
      stop();
      query?.removeEventListener("change", sync);
    };
  }, [count, intervalMs]);
  return { phrase: statusPhraseAt(phrases, tick), announcement: announced ? statusPhraseAt(phrases, 0) : "" };
}
